/* eslint-disable react-hooks/exhaustive-deps */
import { Group } from '@eagle/core-data-types';
import { Box, Divider, FormControl, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { compact, sortBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { T_MANY, T_ONE } from '../../constants';
import { usePromise, useResponsiveText } from '../../hooks';
import { useFetchAllCache } from '../../hooks/use-cache';
import { Nullable } from '../../types';
import { CacheDataTypes } from '../../types/cache';
import { filterDeletedCache, getErrorReason, innerText } from '../../util';
import { ConfirmDialogRadio } from '../confirm-dialog/confirm-dialog-radio';
import { ErrorMessage } from '../error-message';
import { GroupsEditor } from '../groups-editor';
import { MiddleSpinner } from '../middle-spinner';
import { RadioButtonOption } from '../radio-button-set/radio-button-set';
import { EntityAccessProps, PatchType, RadioButtons } from './entity-access.types';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function EntityAccess<T, U = unknown>({
  apiUrl,
  buildPatchRequest,
  disabled,
  entityDisplay,
  failureText,
  groupIds,
  invalidateCache,
  label,
  noConfirmDialog,
  noOptionsText,
  onSuccess,
  successText,
  title,
}: EntityAccessProps<T, U>): JSX.Element {
  const { t } = useTranslation(['common']);
  const { axios } = useAuthenticated();
  const groupsCache = useFetchAllCache(CacheDataTypes.GROUP);
  const [groups, groupsError, groupsState] = usePromise(
    filterDeletedCache<Group>(groupsCache),
    [groupsCache],
  );

  const { enqueueSnackbar } = useSnackbar();
  const options: RadioButtonOption[] = [
    {
      label: t('common:component.entity-access.heading.filtered'),
      value: RadioButtons.FILTERED,
    },
    {
      label: t('common:component.entity-access.heading.all-groups'),
      value: RadioButtons.ALL_GROUPS,
    },
    {
      label: t('common:component.entity-access.heading.no-groups'),
      value: RadioButtons.NO_GROUPS,
    },
  ];

  const initialSelected = groupIds !== null ? (!groupIds.length ? 'No groups' : 'Filtered') : 'All groups';
  const [selected, setSelected] = useState(initialSelected);
  const [confirmSelected, setConfirmSelected] = useState(selected);
  const [isLoading, setIsLoading] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  const getAssignedGroupDisplays = (): string[] => {
    if (!groups) return [];
    const groupDisplays = groupIds?.map((id) => {
      const userGroups = groups.find(({ _id }) => _id === id);
      return userGroups?.display;
    });
    return sortBy(compact(groupDisplays));
  };

  const getAllGroupDisplays = (): string[] => {
    if (!groups) return [];
    const groupDisplays = groups.map(({ display }) => display);
    return sortBy(compact(groupDisplays));
  };

  const getTags = (): string[] => {
    if (confirmSelected === RadioButtons.ALL_GROUPS) return getAllGroupDisplays();
    if (confirmSelected === RadioButtons.FILTERED) return getAssignedGroupDisplays();
    return [];
  };

  const confirmPrompt = (currentValue: string): string => {
    if (currentValue === RadioButtons.ALL_GROUPS) return t('common:component.entity-access.sub-heading.all-groups');
    if (currentValue === RadioButtons.FILTERED) return t('common:component.entity-access.sub-heading.filtered');
    return t('common:component.entity-access.sub-heading.no-groups');
  };

  const onConfirm = async (selectedValue: string): Promise<void> => {
    if (!groups || !buildPatchRequest) return Promise.resolve();
    const updatedGroups: Nullable<string[]> = selectedValue === RadioButtons.ALL_GROUPS ? null : [];

    if (!apiUrl) {
      buildPatchRequest(updatedGroups);
      setConfirmSelected(selectedValue);
      setTags(getTags());
      return Promise.resolve();
    }
    const request = await axios.patch<U>(apiUrl, buildPatchRequest(updatedGroups))
      .then(async ({ data }) => {
        onSuccess?.(data);
        setConfirmSelected(selectedValue);
        setTags(getTags());
        if (invalidateCache) await invalidateCache();
      })
      .catch(() => {
        setSelected(confirmSelected);
        return new Error();
      });
    if (request?.message) throw request;
  };

  const patchUpdate = async (removeOrDelete: string, updatedKey: string, updatedGroup?: string[]): Promise<void> => {
    if (!buildPatchRequest || !groups) return Promise.resolve();
    const groupUpdated = !updatedGroup ? null : updatedGroup.map((groupDisplay) => groups.filter(({ display }) => display === groupDisplay));
    const updatedIds = groupUpdated ? groupUpdated.flat().map(({ _id }) => _id) : null;

    if (!apiUrl) {
      buildPatchRequest(updatedIds);
      return Promise.resolve();
    }

    await axios.patch<U>(apiUrl, buildPatchRequest(updatedIds))
      .then(async ({ data }) => {
        onSuccess?.(data);
        if (removeOrDelete === PatchType.ADD) enqueueSnackbar(t('common:common.hint.add-success', { entity: updatedKey }), { variant: 'success' });
        if (removeOrDelete === PatchType.DELETE) enqueueSnackbar(t('common:component.entity-access.hint.group-remove-success', { group: updatedKey }), { variant: 'success' });
        if (invalidateCache) await invalidateCache();
      })
      .catch((err) => {
        const { path, reason } = getErrorReason(err);
        const errorMessage = path && reason
          ? new Error(t('common:common.hint.update-failure-v2', { type: updatedKey, path, reason }))
          : removeOrDelete === PatchType.DELETE
            ? new Error(t('common:component.entity-access.hint.group-remove-failure', { group: updatedKey }))
            : new Error(t('common:common.hint.add-failure', { entity: updatedKey }));
        if (removeOrDelete === PatchType.ADD) enqueueSnackbar(<ErrorMessage error={errorMessage} />, { variant: 'error' });
        if (removeOrDelete === PatchType.DELETE) enqueueSnackbar(<ErrorMessage error={errorMessage} />, { variant: 'error' });
        throw errorMessage;
      });
  };

  useEffect(() => {
    setTags(getTags());
  }, [confirmSelected, groups]);

  const titleText = title ?? t('common:common.labels.group-access');
  const titleLength = innerText(titleText).length;
  const promptText = confirmPrompt(selected);
  const promptLength = innerText(promptText).length;

  const titleStyles = useResponsiveText('1.25rem', titleLength, {
    minFontSize: '1rem',
    maxFontSize: '1.25rem',
    textLengthThreshold: 25,
  });

  const promptStyles = useResponsiveText('0.875rem', promptLength, {
    minFontSize: '0.75rem',
    maxFontSize: '0.875rem',
    textLengthThreshold: 30,
  });

  if (groupsState === 'pending') return <MiddleSpinner />;
  if (groupsError) return <ErrorMessage error={groupsError} />;

  return (
    <Stack direction="column" flex={[1, 1]}>
      <Typography
        variant="h6"
        style={{
          fontSize: titleStyles.fontSize,
          lineHeight: titleStyles.lineHeight,
          wordBreak: titleStyles.wordBreak,
          whiteSpace: titleStyles.whiteSpace,
          overflow: titleStyles.overflow,
          textOverflow: titleStyles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      <Box mt={1}>
        <Divider />
      </Box>
      <StyledFormControl>
        <ConfirmDialogRadio
          confirmPrompt={
            <Typography
              variant="body2"
              style={{
                fontSize: promptStyles.fontSize,
                lineHeight: promptStyles.lineHeight,
                wordBreak: promptStyles.wordBreak,
                whiteSpace: promptStyles.whiteSpace,
                overflow: promptStyles.overflow,
                textOverflow: promptStyles.textOverflow,
              }}
            >
              {promptText}
            </Typography>
          }
          disabled={disabled}
          noConfirmDialog={noConfirmDialog}
          onConfirm={(selectedValue) => {
            setIsLoading(true);
            return onConfirm(selectedValue)
              .finally(() => setIsLoading(false));
          }}
          onFailure={() => enqueueSnackbar(failureText ?? t('common:common.hint.update-failure', { entity: entityDisplay }), { variant: 'error' })}
          onSuccess={() => enqueueSnackbar(successText ?? t('common:common.hint.update-success', { entity: entityDisplay }), { variant: 'success' })}
          open={open}
          options={options}
          selected={selected}
          setOpen={setOpen}
          setSelected={setSelected}
        />
      </StyledFormControl>
      <GroupsEditor
        defaultValues={tags ?? getAssignedGroupDisplays()}
        disabled={isLoading || selected !== RadioButtons.FILTERED || open || disabled}
        label={t('common:terms.group', { count: T_MANY })}
        noOptionsText={noOptionsText}
        onChange={() => Promise.resolve()}
        optionOnClick={(existingGroups, updatedGroup) => patchUpdate(PatchType.ADD, existingGroups, updatedGroup)}
        optionOnDelete={(existingGroups, updatedGroup) => patchUpdate(PatchType.DELETE, existingGroups, updatedGroup)}
        options={getAllGroupDisplays()}
        placeholder={label ?? t('common:common.action.add-entity', { entity: t('common:terms.group', { count: T_ONE }) })}
        size="small"
      />
    </Stack>
  );
}
